import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Textarea,
} from "@chakra-ui/react";

import { useForm } from "react-hook-form";
import { CustomToast, Dropdown, FormInput } from "../../../../../components";
import { BussinessTypeOptions, ChannelTypeOptions, IBusinessDetails, YearsInBusinessOptions } from "../../modal";
import { useEffect, useState } from "react";
import { useFetch } from "../../../../../hooks/useFetch";
import { SpeechToText } from "./SpeechToText";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface Props {
  nextStep: () => void;
  prevStep: () => void;
  businessDetails: any;
  setBusinessDetails: (data: IBusinessDetails) => void;
  leadId: number;
  onSuccess?: () => void;
}
export const businessDetailsSchema = yup.object({
  businessType: yup
    .number()
    .nullable()
    .typeError("Business Type is required")
    .required("Business Type is required")
    .moreThan(0, "Business Type is required"),

  yearsInBusiness: yup
    .string()
    .required("Years in Business is required"),

  businessChallenges: yup
    .string()
    .trim()
    .notRequired(),

  existingMarketingBudget: yup
    .string()
    .nullable()
    .test(
      "valid-budget",
      "Budget must be a valid positive number",
      (value) => {
        if (!value) return true; // optional field
        return !isNaN(Number(value)) && Number(value) >= 0;
      }
    ),

  currentMarketingChannels: yup
    .array()
    .of(
      yup.object({
        channelType: yup
          .number()
          .nullable()
          .required("Channel Type is required"),

        budget: yup
          .string()
          .required("Budget is required"),
      })
    )
    .default([]),

  competitors: yup
    .array()
    .of(
      yup.string().trim().required("Competitor name is required")
    )
    .default([]),
});

const BusinessDetails = ({
  nextStep,
  prevStep,
  businessDetails,
  setBusinessDetails,
  leadId,
  onSuccess,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [understoodText, setUnderstoodText] = useState<string | null>(null);
  const [understoodAudioFile, setUnderstoodAudioFile] = useState<File | any | null>(null);
  const [marketingChannels, setMarketingChannels] = useState<
    { channelType: number | null; budget: string }[]
  >([
    {
      channelType: null,
      budget: "",
    },
  ]);
  const [competitors, setCompetitors] = useState([""]);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    register,
    formState: { errors },
  } = useForm<IBusinessDetails>({
    defaultValues: businessDetails,
    resolver: yupResolver(businessDetailsSchema) as any,
  });



  const onSubmit = async (data: IBusinessDetails) => {
    setIsLoading(true);

    const payload = {
      leadId,
      businessType: data.businessType,
      yearsInBusiness: data.yearsInBusiness,
      businessChallenges: data.businessChallenges,

      // optional field
      existingMarketingBudget: data.existingMarketingBudget,

      currentMarketingChannels: marketingChannels.filter(
        x => x.channelType !== null
      ),
      competitors: competitors.filter(
        x => x.trim()
      ),
    };

    const res = await fetchApi(
      "LeadRequirement/LeadReqBusinessDetails",
      "POST",
      payload
    );

    if (res) {
      addToast({ message: "Requirement submitted successfully", status: "success", });
      onSuccess?.();

    }

    setIsLoading(false);
  };



  useEffect(() => {
    const getBusinessDetails = async () => {
      if (!leadId) return;
      const res = await fetchApi(
        `LeadRequirement/LeadReqBusinessDetails/${leadId}`,
        "GET"
      );

      if (res) {
        reset({
          businessType: res.businessType || "",
          yearsInBusiness: res.yearsInBusiness || "",
          // businessChallenges: res.businessChallenges || "",
          businessChallenges: understoodText || "",
          existingMarketingBudget: res.existingMarketingBudget || "",
        });

        setUnderstoodText(res.businessChallenges || "");

        setMarketingChannels(
          res.currentMarketingChannels?.length
            ? res.currentMarketingChannels
            : [{ channelType: null, budget: "" }]
        );

        setCompetitors(
          res.competitors?.length
            ? res.competitors
            : [""]
        );

        setBusinessDetails(res);
      }
    };

    getBusinessDetails();
  }, [leadId]);

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(6, 1fr)" }}
          gap="24px"
        >
          {/* Row 1 */}

          <GridItem colSpan={{ base: 6, md: 2 }}>
            <Dropdown
              isRequired
              control={control}
              name="businessType"
              label="Business Type"
              options={BussinessTypeOptions}
              valueKey="id"
              labelKey="name"
              errors={errors}
            />
          </GridItem>

          <GridItem colSpan={{ base: 6, md: 2 }}>
            <Dropdown
              isRequired
              control={control}
              name="yearsInBusiness"
              label="Years in Business"
              options={YearsInBusinessOptions}
              valueKey="id"
              labelKey="name"
              errors={errors}
            />
          </GridItem>

          <GridItem colSpan={{ base: 6, md: 2 }}>
            <FormInput
              control={control}
              name="existingMarketingBudget"
              type="number"
              label="Existing Marketing Budget"
              placeholder="Enter Existing Marketing Budget"
              errors={errors}
            />
          </GridItem>

          {/* Row 2 */}

          <GridItem colSpan={{ base: 6, md: 3 }}>
            <FormLabel>Current Marketing Channels</FormLabel>

            {marketingChannels.map((item, index) => (
              <Flex key={index} gap={2} mb={3} align="center">
                <Box flex={1}>
                  <Dropdown
                    options={ChannelTypeOptions}
                    valueKey="id"
                    labelKey="name"
                    value={item.channelType}
                    onChange={(value) => {
                      const arr = [...marketingChannels];
                      arr[index].channelType =
                        value === "" ? null : Number(value);
                      setMarketingChannels(arr);
                    }}
                  />
                </Box>

                <Input mt={2}
                  w="150px"
                  placeholder="Budget"
                  value={item.budget}
                  type="number"
                  onChange={(e) => {
                    const arr = [...marketingChannels];
                    arr[index].budget = e.target.value;
                    setMarketingChannels(arr);
                  }}
                />

                <Button
                  size="sm"
                  colorScheme="green"
                  w="36px"
                  minW="36px"
                  onClick={() =>
                    setMarketingChannels([
                      ...marketingChannels,
                      { channelType: null, budget: "" },
                    ])
                  }
                >
                  +
                </Button>

                {marketingChannels.length > 1 && (
                  <Button
                    size="sm"
                    colorScheme="red"
                    w="36px"
                    minW="36px"
                    onClick={() =>
                      setMarketingChannels(
                        marketingChannels.filter((_, i) => i !== index)
                      )
                    }
                  >
                    -
                  </Button>
                )}
              </Flex>
            ))}
          </GridItem>

          <GridItem colSpan={{ base: 6, md: 3 }}>
            <FormLabel>Competitors</FormLabel>

            {competitors.map((item, index) => (
              <Flex key={index} gap={2} mb={4}>
                <Input mt={1}
                  className="chakra-input"
                  placeholder={`Competitor ${index + 1}`}
                  value={item}
                  onChange={(e) => {
                    const arr = [...competitors];
                    arr[index] = e.target.value;
                    setCompetitors(arr);
                  }}
                />

                <Button mt={1}
                  size="sm"
                  colorScheme="green"
                  w="36px"
                  minW="36px"
                  onClick={() =>
                    setCompetitors([...competitors, ""])
                  }
                >
                  +
                </Button>

                {competitors.length > 1 && (
                  <Button mt={1}
                    size="sm"
                    colorScheme="red"
                    w="36px"
                    minW="36px"
                    onClick={() =>
                      setCompetitors(
                        competitors.filter((_, i) => i !== index)
                      )
                    }
                  >
                    -
                  </Button>
                )}
              </Flex>
            ))}
          </GridItem>

          {/* Row 3 */}

          <GridItem colSpan={6}>
            <FormControl >
              <FormLabel className="font_poppins text_md text_medium">
                Business Challenges
              </FormLabel>

              {/* <Textarea placeholder="Business Challenges" {...register("businessChallenges")} className="font_poppins text_regular text_sm" rows={5}/> */}
              <SpeechToText
                setValue={(value) => {
                  setUnderstoodText(value);
                  setValue("businessChallenges", value);
                }}
                placeHolder="Comment On What Did You Understand"
                understoodText={understoodText}
                // setValue={setUnderstoodText}
                setAudioFile={setUnderstoodAudioFile}
              />
            </FormControl>
          </GridItem>
        </Grid>

        {/* FOOTER BUTTONS */}
        <Flex justify="flex-end" gap={3} mt={10}>
          <Button bg="#6B7280" color="#FFFFFF" _hover={{ bg: "#4B5563", }} onClick={prevStep}>
            Back
          </Button>

          <Button type="submit" bg="#4F46E5" color="#FFFFFF" isLoading={isLoading} _hover={{ bg: "#4338CA" }}>
            Submit
          </Button>
        </Flex>
      </form>
    </Box >
  );
};

export default BusinessDetails;